import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import axios from "../../../../utils/axios";
import { useDialog } from "../../../../contexts/DialogContext";
import { showErrorSnackbar, showSuccessSnackbar } from "../../../../redux/slices/snackbar";

const CONFIRM_WORD = "delete";
// Backing up and deleting many users can take a while
const DELETE_TIMEOUT_MS = 10 * 60 * 1000;

const CountTable = ({ items, total }) => (
  <Box sx={{ maxHeight: 220, overflow: "auto" }}>
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Data</TableCell>
          <TableCell align="right">Records</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.table + item.label}>
            <TableCell>{item.label}</TableCell>
            <TableCell align="right">{item.count}</TableCell>
          </TableRow>
        ))}
        {total !== undefined && (
          <TableRow>
            <TableCell>
              <b>Total</b>
            </TableCell>
            <TableCell align="right">
              <b>{total}</b>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Box>
);

function DeleteUsersModal({ radUserIds, onDeleted }) {
  const dispatch = useDispatch();
  const { unSetDialog } = useDialog();

  const [check, setCheck] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [checkError, setCheckError] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setIsChecking(true);
    axios
      .post("/radius/user-delete/check", radUserIds, { timeout: DELETE_TIMEOUT_MS })
      .then((response) => active && setCheck(response.data))
      .catch((err) => active && setCheckError(err.message || "Failed to check users"))
      .finally(() => active && setIsChecking(false));
    return () => {
      active = false;
    };
  }, [radUserIds]);

  const isConfirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD;
  const close = () => unSetDialog("delete_users");

  const onDelete = async () => {
    try {
      setIsDeleting(true);
      await axios.post(
        "/radius/user-delete/delete",
        { radUserIds, confirmText: confirmText.trim(), remarks },
        { timeout: DELETE_TIMEOUT_MS },
      );
      dispatch(
        showSuccessSnackbar(
          `${radUserIds.length} user(s) deleted. Backup saved in Deleted Users History.`,
        ),
      );
      close();
      if (onDeleted) onDeleted();
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || "Failed to delete users"));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isChecking) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={6}>
        <CircularProgress />
        <Typography variant="body2">Checking user data...</Typography>
      </Box>
    );
  }

  if (checkError) {
    return (
      <React.Fragment>
        <Alert severity="error">{checkError}</Alert>
        <DialogActions>
          <Button onClick={close}>Close</Button>
        </DialogActions>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Typography variant="subtitle1" gutterBottom>
        {check.users.length} user(s) selected
      </Typography>
      <Box
        sx={{
          maxHeight: 120,
          overflow: "auto",
          mb: 3,
          p: 2,
          backgroundColor: "#f5f5f5",
          borderRadius: 1,
        }}
      >
        {check.users.map((u) => (
          <Typography variant="body2" key={u.radUserId}>
            <b>{u.userName}</b>
            {u.fullName ? ` - ${u.fullName}` : ""}
            {u.tenantName ? ` (${u.tenantName})` : ""}
          </Typography>
        ))}
      </Box>

      {!check.deletable ? (
        <React.Fragment>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These users cannot be deleted because their data is linked to other records.
          </Alert>
          <CountTable items={check.blockers} />
          <DialogActions>
            <Button onClick={close}>Close</Button>
          </DialogActions>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Alert severity="error" sx={{ mb: 2 }}>
            This permanently deletes the users with their renewals, invoices, payments and session
            history. Every record is backed up first and can be viewed or downloaded from Deleted
            Users History.
          </Alert>
          <CountTable items={check.willDelete} total={check.totalRows} />
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            value={remarks}
            disabled={isDeleting}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{ mt: 4 }}
          />
          <Typography variant="body2" mt={3} mb={2}>
            Type <b>{CONFIRM_WORD}</b> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={CONFIRM_WORD}
            value={confirmText}
            disabled={isDeleting}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isConfirmed && !isDeleting) onDelete();
            }}
          />
          <DialogActions>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              endIcon={isDeleting ? <CircularProgress size={20} /> : null}
              disabled={!isConfirmed || isDeleting}
              onClick={onDelete}
            >
              {isDeleting ? "Backing up & deleting..." : `Delete ${check.users.length} user(s)`}
            </Button>
            <Button disabled={isDeleting} onClick={close}>
              Cancel
            </Button>
          </DialogActions>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default DeleteUsersModal;
